import React, { useRef, useEffect, useCallback, useState} from 'react';
import ModuleSys5 from "components/system5/ModuleSys5.js";
import TrackMidiEffects from 'components/pulled/TrackMidiEffects.js';
import GridTab from 'components/pulled/GridTab.js';
import {useContainerCoords} from 'components/hooks/useContainerCoords.js';
import {useSelection} from "components/pulled/useSelection.js";
import {setSelectedPulledElements} from 'actions/index.js';
import PulledBackground from 'components/pulled/PulledBackground.js';
import PadEffects from 'components/pulled/PadEffects.js';
import GlobalEdges from 'components/pulled/GlobalEdges.js';
import PulledSequencer from 'components/pulled/PulledSequencer.js';
import PulledPad from 'components/pulled/PulledPad.js';
import PadsContainer from "components/pads/PadsContainer.js";
import {useWindows} from "components/windows/useWindows.js";
import {midiEffectOptions, initializeMidiEffect}  from 'components/effects/constants.js';
import {useContextMenu} from "components/dropdowns/useContextMenu.js";
import ContextMenu from "components/dropdowns/ContextMenu.js";
import {setPulledEffects} from 'actions/index.js';
import MenuItem from "components/dropdowns/MenuItem.js";
import PulledEffects from "components/pulled/PulledEffects.js";
import Grid from './Grid.js';
import {connect} from 'react-redux';
import '../../../ExploreGrid.scss';

const Grids = ({
    isExplore,
    scale,
    showEffects,
    pulledEffects,
    setPulledEffects,
    setSelectedPulledElements,
    toggled,
    setSelectedTrack,
    subSequence,
    collapsed
}) => { 

    const ref = useRef(undefined);
    const elements = useRef({});
    const {onContextMenu, dropdownPos, setDropdowPos} = useContextMenu({ref});
    const {sizeRef, size, setSize, ordering, setOrdering} = useWindows({ref});
    const dropdownPosRef = useRef(dropdownPos);

    let [selection, setSelection] = useState(undefined);
    let {getXY} = useContainerCoords({containerRef: ref, scale});
    let {getSelection} = useSelection({elements});

    const onMouseDown = useCallback((e) => {
        let {x,y} = getXY(e);
        setSelection({x1: x, y1: y, x2: x, y2: y});
    }, [setSelection, scale]);

    const onMouseMove = useCallback((e) => {
        let {x,y} = getXY(e);
        setSelection({...selection, x2: x, y2: y});
    }, [selection, setSelection, scale]);

    const onMouseUp = useCallback((e) => {
        if (selection && selection.x2 - selection.x1 > 1) {
            let selectedElements = getSelection(selection);
            let x = {};
            for (let {id} of selectedElements) {
                x[id] = true;
            }
            setSelectedPulledElements(x);
        }
        if (selection) {
            setSelection(undefined);
        }
    }, [selection, setSelection]);

    useEffect(() => {
        if (dropdownPos) {
            dropdownPosRef.current = dropdownPos;
        }
    }, [dropdownPos]);
    
    let rows = [0,1,2,3,4];
    let cols = [0,1,2];
    let {tracks=[]} = subSequence;
    tracks = tracks.filter(x => isExplore || x.pad === undefined);

    const addEffect = useCallback((value) => {
        let effect = initializeMidiEffect(value, subSequence);
        let {x,y} = dropdownPosRef.current;
        effect.x = x;
        effect.y = y;
        subSequence.midiEffects.addEffect(effect);
        setPulledEffects(
            [... pulledEffects,
             effect]);
    }, [subSequence, pulledEffects]);

    let style = {
        width: size.width,
        height: size.height
    };

    const onClick = useCallback((e) => {
        subSequence.sequencerEngine.switchToSubSequence(
            subSequence.id);
    }, [subSequence]);

    const createSequencer = useCallback(() => {
        let sequencer = subSequence.createSequencer();
        let {x,y} = dropdownPosRef.current;
        sequencer.x = x;
        sequencer.y = y;
    }, [subSequence]);

    const createPad = useCallback(() => {
        let pad = subSequence.createPad();
        let {x,y} = dropdownPosRef.current;
        pad.x = x;
        pad.y = y;
    }, [subSequence]);

    if (!subSequence || !subSequence.tracks) {
        return [];
    }

    let inner = (
        <div>
          {!isExplore && <PulledBackground
                           selection={selection}
                           scale={scale}
                           elements={elements}
                           containerRef={ref}
                         />}
          <ContextMenu className="midi-context" dropdownPos={dropdownPos ? {
              x: dropdownPos.x / scale,
              y: dropdownPos.y / scale } : undefined}>
            <MenuItem
              onMouseDown={createSequencer}>
              New Sequencer
            </MenuItem>
            <MenuItem
              onMouseDown={createPad}>
              New Pad
            </MenuItem>
            <div className="context-divider"/>
            {midiEffectOptions.map(
                ({label, value})=>
                    <MenuItem
                      onMouseDown={() => addEffect(value)}
                    >{label}</MenuItem>)}
          </ContextMenu>
          {isExplore &&
           <div className="grid-rows">
             {rows.map(
                 row =>
                     <div key={row} className="grid-row">
                       {cols.map(
                           col => tracks[row*3 + col] ?
                               <Grid
                                 scale={scale}
                                 toggled={toggled}
                                 setSelectedTrack={setSelectedTrack}
                                 key={row*3 + col}
                                 track={tracks[row*3 + col]}
                                 row={row} col={col}/> : '')}
                     </div>)}
           </div>
          }
          <PulledEffects
            scale={scale}
            sizeRef={sizeRef}
            setSize={setSize}
            size={size}
            ordering={ordering}
            setOrdering={setOrdering}
            playhead={true}
            elements={elements}
            containerRef={ref}
            noWrapper={true}
            sequencerEngine={subSequence.sequencerEngine}
            subSequence={subSequence}/>
          {!isExplore && subSequence.pads.map(
              pad => <PulledPad
              key={pad.id}
              scale={scale}
              size={size}
              ordering={ordering}
              setOrdering={setOrdering}
              setSize={setSize}
              sizeRef={sizeRef}
              elements={elements}
              scrollContainer={ref}
              pad={pad}
   />)}
          {!isExplore && <GlobalEdges
                           containerRef={ref}
                     scale={scale}
                     subSequence={subSequence}/>}
          {!isExplore && subSequence.tracks.map(
              track => 
                  <TrackMidiEffects
                    scale={scale}
                    ordering={ordering}
                    setOrdering={setOrdering}
                    size={size}
                    setSize={setSize}
                    sizeRef={sizeRef}
                    elements={elements}
                    containerRef={ref}
                    track={track}/>)}
          {!isExplore && subSequence.pads
           .filter(pad => pad.effectRack)
           .map(
              pad => 
                  <PadEffects
                    scale={scale}
                    ordering={ordering}
                    setOrdering={setOrdering}
                    size={size}
                    setSize={setSize}
                    sizeRef={sizeRef}
                    elements={elements}
                    containerRef={ref}
                    pad={pad}/>)}
          {!isExplore && subSequence.sequencers.map(
              sequencer =>
                  <PulledSequencer
                    ordering={ordering}
                    setOrdering={setOrdering}
                    scrollContainer={ref}
                    scale={scale}
                    size={size}
                    setSize={setSize}
                    sizeRef={sizeRef}
                    elements={elements}
                    sequencer={sequencer}/>)}
        </div>);
    
    return (
        <div
          onClick={onClick}
          onMouseDown={onMouseDown}
          onMouseMove={onMouseMove}
          onMouseUp={onMouseUp}
          style={isExplore || collapsed ? {} : style}
          onContextMenu={onContextMenu}
          ref={ref} className={collapsed ? "grids collapsed" : "grids not-collapsed"}>
          <GridTab subSequence={subSequence}/>
          {collapsed ? <ModuleSys5 subSequence={subSequence} /> : inner}
        </div>
    );
}

const mapStateToProps = (state, props) => {
    return {
        pulledEffects: state.effects.pulledEffects,
        pulledSequencers: state.pads.sequencers,
        numTracks: props.subSequence.tracks && props.subSequence.tracks.length,
        collapsed: state.pads.modulesCollapsed[props.subSequence.id]
    };
};

const mapDispatchToProps = (dispatch) => {
    return {
        setPulledEffects: x => dispatch(setPulledEffects(x)),
        setSelectedPulledElements: x => dispatch(
            setSelectedPulledElements(x))
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(Grids);
